import * as React from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import { Control, Controller } from 'react-hook-form';
import { Autocomplete, FormHelperText, Typography } from '@mui/material';
import TextField from './TextField';
import { tokens } from '../../theme';

interface CustomMultipleDropDownProps {
  options: { value: string | number; label: string }[];
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium";
  label?: string;
  labelAbove?: boolean;
  variant?: "filled" | "outlined" | "standard";
  disableSearch?: boolean;
  name?: string;
  onChangeSelect?: (e: any, value: any) => any; 
  control?: Control<any, any>;
  rules?: object;
  disabled?: boolean;
  sortOptions?: boolean;
  placeholderText?: string | "";
  allOptionValue?: string | number;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CustomMultipleDropDown: React.FC<CustomMultipleDropDownProps> = ({
  options,
  size = "medium",
  color,
  label,
  labelAbove = false,
  variant = "outlined",
  disableSearch = false,
  name,
  control,
  onChangeSelect,
  rules,
  placeholderText,
  disabled = false,
  sortOptions = true,
  allOptionValue = "ALL",
  ...props
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // ADDED: The same custom palette from CustomDropdownField
  const ct = {
    inputBg:     isDark ? "rgba(255,255,255,.04)" : "#f8fafc",
    inputBorder: isDark ? "rgba(255,255,255,.10)" : "#cbd5e1",
    accent:      "#14b8a6",
    textPrimary: isDark ? "#e2e8f0" : "#141720",
    textSec:     isDark ? "#64748b" : "#64748b",
    dropBg:      isDark ? "#0c1624" : "#ffffff",
    cardBg:      isDark ? "#1c2030" : "#f1f5f9",
    border:      isDark ? "rgba(255,255,255,.07)" : "#e2e8f0",
    inputBorderFocus: "rgba(20,184,166,.5)",
  };

  const [fieldValueArray, setFieldValueArray] = React.useState<string[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [limitTags, setLimitTags] = React.useState(2);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const available = el.offsetWidth - 90;
      setLimitTags(Math.max(1, Math.floor(available / 85)));
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  const dropdownScrollbarSx = {
    scrollbarGutter: "stable",
    scrollbarWidth: "thin",
    scrollbarColor: isDark ? "rgba(255,255,255,0.22) transparent" : "#94A3B8 transparent",
    "&::-webkit-scrollbar": { width: "6px", height: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { 
      background: isDark ? "rgba(255,255,255,0.22)" : "#94A3B8", 
      borderRadius: 4 
    },
  };

  const processedOptions = sortOptions
    ? [...options].sort((a, b) => a.label.localeCompare(b.label))
    : options;

  const handleChange = (event: any, field: any, newValue: { value: string | number; label: string }[]) => {
    let filteredValue = newValue;

    const hasAll = newValue.some(option => option.value === allOptionValue);
    const previousHasAll = Array.isArray(field.value) && field.value.includes(allOptionValue);

    if (hasAll && !previousHasAll) {
      filteredValue = newValue.filter(option => option.value === allOptionValue);
    } else if (hasAll && newValue.length > 1) {
      filteredValue = newValue.filter(option => option.value !== allOptionValue);
    }

    const valueArray = filteredValue.map((newVal) => newVal.value);

    field.onChange(valueArray);
    if (onChangeSelect) {
      onChangeSelect(event, valueArray);
    }
  };

  const isRequired = () => {
    if (rules && typeof rules === "object" && "required" in rules && label) {
      return true;
    }
    return false;
  };

  const getLabel = () => {
    if (isRequired()) {
      return (
        <span>
          {label}{" "}
          <span style={{ color: colors.success[200], fontSize: "18px" }}>*</span>
        </span>
      );
    }
    return label;
  };

  return (
    <Controller
      name={name || ""}
      control={control}
      rules={rules ? rules : {}}
      render={({ field: { ref, value, ...field }, fieldState }) => {
        const selectedOptions = processedOptions?.filter(option => Array.isArray(value) && value.includes(option.value));
        return (
          <Box ref={containerRef} sx={{ width: '100%' }}>
            {labelAbove && (
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: theme.palette.text.secondary, mb: 0.7, lineHeight: "16px" }}>
                {label}
              </Typography>
            )}
            <Autocomplete
              multiple
              options={processedOptions}
              value={selectedOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              disableCloseOnSelect
              limitTags={limitTags}
              disabled={disabled}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              componentsProps={{
                paper: {
                  sx: {
                    overflow: "hidden",
                    backgroundColor: ct.dropBg,
                    border: `1px solid ${ct.inputBorder}`,
                    borderRadius: "12px",
                    boxShadow: "0 20px 40px -10px rgba(0,0,0,.6)",
                  },
                },
                popper: {
                  modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
                },
              }}
              ListboxProps={{
                sx: {
                  maxHeight: 260,
                  overflowY: "auto",
                  p: 0.7,
                  ...dropdownScrollbarSx,
                  "& .MuiAutocomplete-option": { borderRadius: "8px" },
                  "& .MuiAutocomplete-option.Mui-focused": { borderRadius: "8px" },
                },
              }}
              onChange={(e, newValue) => handleChange(e, field, newValue)}
              renderTags={(tagValue, getTagProps) => {
                // MUI passes all tags when focused — always enforce our own limit
                const visible = tagValue.slice(0, limitTags);
                const hiddenCount = selectedOptions.length - visible.length;
                return (
                  <>
                    {visible.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      return (
                        <Chip
                          {...tagProps}
                          label={option.label}
                          title={option.label}
                          sx={{
                            flexShrink: 1,
                            maxWidth: '130px',
                            minWidth: 0,
                            borderRadius: '999px',
                            backgroundColor: isDark ? 'rgba(20,184,166,0.16)' : 'rgba(13,148,136,0.12)',
                            color: `${ct.accent} !important`,
                            border: `1px solid ${isDark ? 'rgba(20,184,166,0.26)' : 'rgba(13,148,136,0.25)'}`,
                            fontWeight: 600,
                            '& .MuiChip-label': { pl: '6px', pr: '6px' },
                            '& .MuiChip-deleteIcon': {
                              color: isDark ? 'rgba(20,184,166,0.7)' : 'rgba(13,148,136,0.7)',
                              '&:hover': { color: ct.accent },
                            },
                          }}
                        />
                      );
                    })}
                    {hiddenCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '24px',
                          minWidth: '34px',
                          borderRadius: '12px',
                          px: 0.75,
                          backgroundColor: isDark ? 'rgba(20,184,166,0.18)' : 'rgba(20,184,166,0.12)',
                          color: ct.accent,
                          border: `1px solid ${ct.accent}`,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                        }}
                      >
                        +{hiddenCount}
                      </Box>
                    )}
                  </>
                );
              }}
              renderOption={(props, option, { selected }) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: selected
                      ? isDark ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.08)'
                      : 'transparent',
                    '&.Mui-focused, &:hover': {
                      backgroundColor: selected
                        ? isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.14)'
                        : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      mr: 1,
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: selected ? ct.accent : 'transparent',
                      border: selected ? `2px solid ${ct.accent}` : 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      color: '#fff',
                      fontWeight: 700,
                      lineHeight: 1,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {selected ? '✓' : ''}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.875rem',
                      color: selected ? ct.accent : ct.textPrimary,
                      fontWeight: selected ? 600 : 400,
                    }}
                  >
                    {option.label}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={labelAbove ? "" : (getLabel() || label)}
                  variant={variant}
                  size={size}
                  placeholder={selectedOptions.length === 0 && !isOpen ? (placeholderText || (labelAbove ? label || "" : "")) : ""}
                  color={color}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: isOpen || selectedOptions.length > 0,
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : null}
                  disabled={disabled}
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: '53px',
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      gap: '4px',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                      display: 'none',
                    },
                    '& .MuiInputBase-input': {
                      padding: '0 !important',
                      minWidth: '0 !important',
                    },
                    '& .MuiChip-root.Mui-disabled': {
                      color: isDark ? '' : 'black'
                    },
                    '& label': {
                      color: isDark ? '' : colors.primary[100],
                    },
                    '& label.Mui-disabled': {
                      color: isDark ? '' : colors.grey[400],
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: isDark ? '' : colors.primary[100],
                      }
                    },
                    '& .MuiInputBase-root.Mui-disabled': {
                      '& fieldset': {
                        borderColor: isDark ? '' : colors.grey[500],
                      }
                    },
                  }}
                />
              )}
            />
          </Box>
        );
      }}
    />
  );
}

export default CustomMultipleDropDown;